import { hasValue } from "../../common/helpers/Utils";
import {
  findParentTagCandidate,
  getHwioChannelPrefix,
  isHwioTag,
  lookupIdToName,
  matchesSourceType,
} from "./Helpers";
import {
  getIncompatibleColumnMessage,
  isColumnCompatibleWithTag,
  MODBUS_TCP_SOURCE_TYPE_NAMES,
} from "./SourceTypeColumns";

/** Shown in HardWiredIO-only columns for tags of any other source type. */
export const NOT_HWIO_MESSAGE = getIncompatibleColumnMessage("HwioIOType");

/**
 * Resolves a HardWiredIO tag's I/O Type to its TagType InternalName, e.g. 'AI' or 'DO'.
 * @param {*} row The tag or grid row.
 * @param {*} lookupValues The lookup values from LookupValueContext.
 * @returns {string} The InternalName, or '' when unset or unresolvable.
 */
const getHwioIOTypeName = (row, lookupValues) => {
  if (!hasValue(row.TagTypeId)) {
    return "";
  }
  return lookupIdToName(row.TagTypeId, "TagType", lookupValues) || "";
};

/**
 * Converts a stored HardWiredIO channel number to the 1-based value shown to users. Channel numbers
 * are stored 0-based but the shared tag editor labels them from 1 ("AI 1" is stored as 0).
 * @param {*} storedChannelNumber The raw ChannelNumber field.
 * @returns {number|string} The 1-based channel number, or '' when unset or non-numeric.
 */
const toDisplayChannelNumber = (storedChannelNumber) => {
  if (!hasValue(storedChannelNumber)) {
    return "";
  }
  const parsed = Number(storedChannelNumber);
  return Number.isNaN(parsed) ? "" : parsed + 1;
};

export const generateDeviceInfoCell = (row, lookupValues) => {
  if (!row || !row.SourceType) {
    return "No Source";
  }
  const sourceTypeName = row.SourceType;

  // CDL
  if (sourceTypeName === "CDL") {
    try {
      const cdlInfo = JSON.parse(row.DeviceParams);
      const value = `${cdlInfo.mid ?? ""}/${cdlInfo.pid ?? ""};${cdlInfo.mask ?? ""}`;
      return value;
    } catch (e) {
      return "Invalid DeviceParams";
    }
  }
  // HWIO
  else if (isHwioTag(row, lookupValues)) {
    // HWIO channel numbers are stored from 0 but displayed from 1.
    const value = `${row.PortNumber ?? ""}/${getHwioIOTypeName(row, lookupValues)}${toDisplayChannelNumber(row.ChannelNumber)}`;
    return value;
  }
  // ModbusRTU
  else if (
    sourceTypeName === "ModbusRTU232" ||
    sourceTypeName === "ModbusRTU485"
  ) {
    try {
      const modbusRTUInfo = JSON.parse(row.DeviceParams);
      const value = `${modbusRTUInfo.connection.slave_id ?? ""}/${modbusRTUInfo.regnum ?? ""};${modbusRTUInfo.connection.port_number ?? ""}`;
      return value;
    } catch (e) {
      return "Invalid DeviceParams";
    }
  }
  // ModbusTCP
  else if (sourceTypeName === "ModbusTCP") {
    try {
      const modbusTCPInfo = JSON.parse(row.DeviceParams);
      const value = `${modbusTCPInfo.connection.slave_id ?? ""}/${modbusTCPInfo.regnum ?? ""};${modbusTCPInfo.connection.ip ?? ""}:${modbusTCPInfo.connection.port ?? ""}`;
      return value;
    } catch (e) {
      return "Invalid DeviceParams";
    }
  }
  // SystemMetric
  else if (sourceTypeName === "SystemMetric") {
    return row.DeviceParams ?? "";
  }
  // FunctionLibrary
  else if (sourceTypeName === "Function Library") {
    try {
      if (!row.Transform) {
        return "Invalid Transform";
      }
      if (row.Transform.startsWith("scriptTemplate")) {
        const parsedJSON = JSON.parse(row.Transform.substr(15));
        const funcName = parsedJSON.templateName ?? "Custom Function";
        return funcName;
      } else {
        return "Custom Function";
      }
    } catch (e) {
      return "Invalid Transform";
    }
  }
  // J1939
  else if (sourceTypeName === "J1939") {
    try {
      const value = row.DeviceParams ?? "";
      return value;
    } catch (e) {
      return "Invalid DeviceParams";
    }
  }
  // otherwise nothing
  return "";
};

export const generateDisplayFormatCell = (row) => {
  const value = row.DisplayFormat ?? "";
  if (
    window.Enbase &&
    window.Enbase.Common &&
    window.Enbase.Common.Tag &&
    window.Enbase.Common.Tag.ParseCodeLegend(value)
  ) {
    return "Code Legend";
  } else {
    return value;
  }
};

export const generateTransformCell = (row) => {
  const value = row.Transform ?? "";
  if (value.startsWith("scriptTemplate")) {
    try {
      const funcName =
        JSON.parse(value.substr(15)).templateName ?? "Custom Function";
      return funcName;
    } catch (e) {
      return "Custom Function";
    }
  } else if (value.startsWith("table")) {
    return "Table";
  } else if (value.startsWith("script")) {
    return "Custom Function";
  } else return "";
};

export const generateHiddenCell = (row) => {
  try {
    const dynamicProperties = row?.DynamicProperties
      ? JSON.parse(row.DynamicProperties)
      : {};
    return dynamicProperties?.isHidden === true;
  } catch {
    return false;
  }
};

export const generateSafetyShutdownCell = (row) => {
  try {
    const dynamicProperties = row?.DynamicProperties
      ? JSON.parse(row.DynamicProperties)
      : {};
    return dynamicProperties?.flags?.["Safety Shutdown"] === true;
  } catch {
    return false;
  }
};

/**
 * Reads a HardWiredIO tag's I/O Type, stored as a TagType lookup value id in TagTypeId.
 * @param {*} row The tag or grid row.
 * @param {*} lookupValues The lookup values from LookupValueContext.
 * @returns {string} The TagType InternalName, '' when unset, or NOT_HWIO_MESSAGE for other sources.
 */
export const generateHwioIOTypeCell = (row, lookupValues) => {
  if (!isColumnCompatibleWithTag("HwioIOType", row, lookupValues)) {
    return getIncompatibleColumnMessage("HwioIOType");
  }
  return getHwioIOTypeName(row, lookupValues);
};

/**
 * Reads a HardWiredIO tag's Channel Number, stored 0-based in ChannelNumber but displayed 1-based
 * and prefixed with the I/O type, exactly as the shared tag editor labels it ("AI 1").
 * @param {*} row The tag or grid row.
 * @param {*} lookupValues The lookup values from LookupValueContext.
 * @returns {string} The prefixed channel label, '' when unset, or the incompatible-column message.
 */
export const generateHwioChannelNumberCell = (row, lookupValues) => {
  if (!isColumnCompatibleWithTag("HwioChannelNumber", row, lookupValues)) {
    return getIncompatibleColumnMessage("HwioChannelNumber");
  }

  const displayChannelNumber = toDisplayChannelNumber(row.ChannelNumber);
  if (displayChannelNumber === "") {
    return "";
  }

  const prefix = getHwioChannelPrefix(row, lookupValues);
  return prefix
    ? `${prefix} ${displayChannelNumber}`
    : String(displayChannelNumber);
};

/**
 * Reads a HardWiredIO tag's Board Number, stored in the generic PortNumber field.
 * @param {*} row The tag or grid row.
 * @param {*} lookupValues The lookup values from LookupValueContext.
 * @returns {*} The board number, '' when unset, or the incompatible-column message.
 */
export const generateHwioBoardNumberCell = (row, lookupValues) => {
  if (!isColumnCompatibleWithTag("HwioBoardNumber", row, lookupValues)) {
    return getIncompatibleColumnMessage("HwioBoardNumber");
  }
  return hasValue(row.PortNumber) ? row.PortNumber : "";
};

export const generateModbusRegisterCell = (row, lookupValues) => {
  if (!isColumnCompatibleWithTag("ModbusRegister", row, lookupValues)) {
    return getIncompatibleColumnMessage("ModbusRegister");
  }
  try {
    return JSON.parse(row.DeviceParams).regnum;
  } catch (e) {
    return "Invalid DeviceParams";
  }
};

export const generateModbusIPCell = (row, lookupValues) => {
  if (!isColumnCompatibleWithTag("ModbusIP", row, lookupValues)) {
    return getIncompatibleColumnMessage("ModbusIP");
  }
  try {
    return JSON.parse(row.DeviceParams).connection.ip;
  } catch (e) {
    return "Invalid DeviceParams";
  }
};

export const generateModbusPortCell = (row, lookupValues) => {
  if (!isColumnCompatibleWithTag("ModbusPort", row, lookupValues)) {
    return getIncompatibleColumnMessage("ModbusPort");
  }
  try {
    // TCP connections carry a `port`; RTU connections carry a `port_number`.
    const connection = JSON.parse(row.DeviceParams).connection;
    return matchesSourceType(row, MODBUS_TCP_SOURCE_TYPE_NAMES, lookupValues)
      ? connection.port
      : connection.port_number;
  } catch (e) {
    return "Invalid DeviceParams";
  }
};

export const generateModbusSlaveIdCell = (row, lookupValues) => {
  if (!isColumnCompatibleWithTag("ModbusSlave", row, lookupValues)) {
    return getIncompatibleColumnMessage("ModbusSlave");
  }
  try {
    return JSON.parse(row.DeviceParams).connection.slave_id;
  } catch (e) {
    return "Invalid DeviceParams";
  }
};

export const generateModbusMapIDCell = (row) => {
  return row.TagIdentifier * 2 + 40001;
};

const getSetpointFromDeviceParams = (
  row,
  setpointType,
  columnId,
  lookupValues
) => {
  try {
    if (!isColumnCompatibleWithTag(columnId, row, lookupValues)) {
      return getIncompatibleColumnMessage(columnId);
    }
    const parsedDeviceParams = row.DeviceParams
      ? JSON.parse(row.DeviceParams)
      : null;
    if (
      !parsedDeviceParams.properties_to_sync ||
      !parsedDeviceParams.properties_to_sync.properties
    ) {
      return "Not Configured";
    }
    if (parsedDeviceParams.properties_to_sync.sync_direction === "None") {
      return "Not Configured";
    }
    if (!parsedDeviceParams.properties_to_sync.properties[setpointType]) {
      return "Not Configured";
    }
    return parsedDeviceParams.properties_to_sync.properties[setpointType]
      .regnum;
  } catch (e) {
    return "Invalid DeviceParams";
  }
};

export const generateLoSetpointCell = (row, lookupValues) => {
  return getSetpointFromDeviceParams(
    row,
    "LoLimit",
    "LoSetpoint",
    lookupValues
  );
};

export const generateLoLoSetpointCell = (row, lookupValues) => {
  return getSetpointFromDeviceParams(
    row,
    "LoLoLimit",
    "LoLoSetpoint",
    lookupValues
  );
};
export const generateHiSetpointCell = (row, lookupValues) => {
  return getSetpointFromDeviceParams(
    row,
    "HiLimit",
    "HiSetpoint",
    lookupValues
  );
};

export const generateHiHiSetpointCell = (row, lookupValues) => {
  return getSetpointFromDeviceParams(
    row,
    "HiHiLimit",
    "HiHiSetpoint",
    lookupValues
  );
};

export const generateCodeLegendCell = (row, codeLegends) => {
  if (!row.CodeLegendId) {
    return "";
  }
  const codeLegend = codeLegends.find(
    (legend) => legend.id === row.CodeLegendId
  );
  return codeLegend ? codeLegend.label : "Unknown Code Legend";
};

export const generateParentTagCell = (row, allTags) => {
  if (!hasValue(row?.ParentTagIdentifier)) {
    return null;
  }

  const matchedTag = findParentTagCandidate(row, allTags);

  if (matchedTag) {
    return {
      tagIdentifier: row.ParentTagIdentifier,
      tagName: matchedTag.TagName,
      resolved: true,
    };
  }

  return {
    tagIdentifier: row.ParentTagIdentifier,
    tagName: null,
    resolved: false,
  };
};

export const generateSyncDirectionCell = (row, lookupValues) => {
  try {
    if (!isColumnCompatibleWithTag("SyncDirection", row, lookupValues)) {
      return getIncompatibleColumnMessage("SyncDirection");
    }
    const parsedDeviceParams = JSON.parse(row.DeviceParams);
    if (
      !parsedDeviceParams.properties_to_sync ||
      !parsedDeviceParams.properties_to_sync.sync_direction
    ) {
      return "Not Configured";
    }
    return parsedDeviceParams.properties_to_sync.sync_direction;
  } catch (e) {
    return "Invalid DeviceParams";
  }
};

export const generateModbusRegisterPLCCell = (row) => {
  return row.TagIdentifier * 2 + 400000;
};

export const generateModbusRegisterRTUCell = (row) => {
  return row.TagIdentifier * 2 + 400001;
};
